import React, { useMemo } from 'react'
import { Box, Flex, Icon, Text, Loading } from '../../atoms'
import { useTheme } from 'styled-components'
import { SavedBuildingPermitsGrid } from './SavedBuildingPermitsGrid'
import {
  PermitsContainer,
  PermitsStatsSection,
  PermitsMetricCard,
  PermitsMetricValue,
  PermitsMetricLabel,
  PermitsEmptyState,
  PermitsEmptyStateIcon,
  PermitsEmptyStateText,
} from './SavedBuildingPermits.styles'
import type { BuildingPermit } from '@popety-io/types'
import type { TFunction } from 'i18next'

interface SavedBuildingPermitsProps {
  permits: BuildingPermit[]
  isLoading?: boolean
  onPermitClick: (permit: BuildingPermit) => void
  onRemovePermit?: (permitId: string) => void
  t?: TFunction
}

export const SavedBuildingPermits: React.FC<SavedBuildingPermitsProps> = ({
  permits,
  isLoading,
  onPermitClick,
  onRemovePermit,
  t,
}) => {
  const theme = useTheme()

  // Fallback for missing t function
  const translate = t || ((key: string) => key.split('.').pop() || key)

  // Calculate statistics for saved permits
  const stats = useMemo(() => {
    if (permits.length === 0) {
      return {
        totalSaved: 0,
        byCategory: {
          development: 0,
          renovation: 0,
          transformation: 0,
          elevation: 0,
          regularisation: 0,
        },
        byStatus: {
          approved: 0,
          pending: 0,
          rejected: 0,
          completed: 0,
          instruction: 0,
          enregistrement: 0,
        },
        mostCommonCategory: '—',
        mostCommonStatus: '—',
        recentCount: 0,
      }
    }

    const byCategory: Record<string, number> = {}
    const byStatus: Record<string, number> = {}

    permits.forEach((permit) => {
      if (permit.category) {
        byCategory[permit.category] = (byCategory[permit.category] || 0) + 1
      }
      if (permit.status) {
        byStatus[permit.status] = (byStatus[permit.status] || 0) + 1
      }
    })

    const mostCommonCategory =
      Object.keys(byCategory).length > 0
        ? Object.entries(byCategory).sort((a, b) => b[1] - a[1])[0][0]
        : '—'

    const mostCommonStatus =
      Object.keys(byStatus).length > 0
        ? Object.entries(byStatus).sort((a, b) => b[1] - a[1])[0][0]
        : '—'

    // Count recent permits (last 6 months)
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
    const recentCount = permits.filter((permit) => {
      if (!permit.filingDate) return false
      const filingDate =
        permit.filingDate instanceof Date
          ? permit.filingDate
          : new Date(permit.filingDate)
      return !isNaN(filingDate.getTime()) && filingDate > sixMonthsAgo
    }).length

    return {
      totalSaved: permits.length,
      byCategory: {
        development: byCategory.development || 0,
        renovation: byCategory.renovation || 0,
        transformation: byCategory.transformation || 0,
        elevation: byCategory.elevation || 0,
        regularisation: byCategory.regularisation || 0,
      },
      byStatus: {
        approved: byStatus.approved || 0,
        pending: byStatus.pending || 0,
        rejected: byStatus.rejected || 0,
        completed: byStatus.completed || 0,
        instruction: byStatus.instruction || 0,
        enregistrement: byStatus.enregistrement || 0,
      },
      mostCommonCategory,
      mostCommonStatus,
      recentCount,
    }
  }, [permits])

  if (isLoading) {
    return (
      <PermitsContainer>
        <Box padding="48px" textAlign="center">
          <Loading />
          <Text color={theme.colors.text.secondary}>
            {translate('buildingPermits.saved.loading')}
          </Text>
        </Box>
      </PermitsContainer>
    )
  }

  if (permits.length === 0) {
    return (
      <PermitsContainer>
        <PermitsEmptyState>
          <PermitsEmptyStateIcon>
            <Icon
              name="bookmark"
              size="large"
              color={theme.colors.text.tertiary}
            />
          </PermitsEmptyStateIcon>
          <PermitsEmptyStateText>
            <Text weight="semibold">
              {translate('buildingPermits.saved.emptyTitle')}
            </Text>
            <Text color={theme.colors.text.secondary}>
              {translate('buildingPermits.saved.emptyDescription')}
            </Text>
          </PermitsEmptyStateText>
        </PermitsEmptyState>
      </PermitsContainer>
    )
  }

  return (
    <PermitsContainer>
      {/* Statistics Section */}
      <PermitsStatsSection>
        <Flex gap="16px" wrap="wrap">
          <PermitsMetricCard>
            <PermitsMetricLabel>
              <Icon name="bookmark" size="small" />
              {translate('buildingPermits.saved.totalSaved')}
            </PermitsMetricLabel>
            <PermitsMetricValue>{stats.totalSaved}</PermitsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translate('buildingPermits.saved.buildingPermits')}
            </Text>
          </PermitsMetricCard>

          <PermitsMetricCard $accent>
            <PermitsMetricLabel>
              <Icon name="trending-up" size="small" />
              {translate('buildingPermits.saved.mostCommonCategory')}
            </PermitsMetricLabel>
            <PermitsMetricValue color={theme.colors.primary}>
              {stats.mostCommonCategory}
            </PermitsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {stats.byCategory[
                stats.mostCommonCategory as keyof typeof stats.byCategory
              ] || 0}{' '}
              {translate('buildingPermits.saved.permits')}
            </Text>
          </PermitsMetricCard>

          <PermitsMetricCard>
            <PermitsMetricLabel>
              <Icon name="check-circle" size="small" />
              {translate('buildingPermits.saved.approved')}
            </PermitsMetricLabel>
            <PermitsMetricValue>{stats.byStatus.approved}</PermitsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translate('buildingPermits.saved.permits')}
            </Text>
          </PermitsMetricCard>

          <PermitsMetricCard>
            <PermitsMetricLabel>
              <Icon name="clock" size="small" />
              {translate('buildingPermits.saved.pending')}
            </PermitsMetricLabel>
            <PermitsMetricValue>{stats.byStatus.pending}</PermitsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translate('buildingPermits.saved.permits')}
            </Text>
          </PermitsMetricCard>

          <PermitsMetricCard>
            <PermitsMetricLabel>
              <Icon name="check-square" size="small" />
              {translate('buildingPermits.saved.completed')}
            </PermitsMetricLabel>
            <PermitsMetricValue>{stats.byStatus.completed}</PermitsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translate('buildingPermits.saved.permits')}
            </Text>
          </PermitsMetricCard>

          <PermitsMetricCard>
            <PermitsMetricLabel>
              <Icon name="calendar" size="small" />
              {translate('buildingPermits.saved.recentActivity')}
            </PermitsMetricLabel>
            <PermitsMetricValue>{stats.recentCount}</PermitsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translate('buildingPermits.saved.lastSixMonths')}
            </Text>
          </PermitsMetricCard>

          <PermitsMetricCard>
            <PermitsMetricLabel>
              <Icon name="tag" size="small" />
              {translate('buildingPermits.saved.commonStatus')}
            </PermitsMetricLabel>
            <PermitsMetricValue>{stats.mostCommonStatus}</PermitsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              {translate('buildingPermits.saved.mostFrequent')}
            </Text>
          </PermitsMetricCard>
        </Flex>
      </PermitsStatsSection>

      {/* Grid of saved permits cards */}
      <SavedBuildingPermitsGrid
        permits={permits}
        onPermitClick={onPermitClick}
        onRemovePermit={onRemovePermit}
      />
    </PermitsContainer>
  )
}
